import { WhatsApp24 } from "@/components/site/WhatsApp24";
import { Instagram, Facebook } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BrandLockup } from "./BrandLockup";
import { SITE } from "@/lib/site";

export function SiteFooter() {
  const { t } = useTranslation();
  const cols = [
    {
      title: t("footer.explore"),
      links: [
        { to: "/tours", label: t("footer.toursMx") },
        { to: "/flights", label: t("footer.needFlight", "¿Necesitas vuelo?") },
        { to: "/blog", label: t("footer.travelGuides") },
      ],
    },
    {
      title: t("footer.company"),
      links: [
        // "Estado del sistema" vive sólo en el panel administrativo.
        { to: "/about", label: t("footer.about") },
        { to: "/contact", label: t("footer.contact") },
      ],
    },
    {
      title: t("footer.help"),
      links: [
        { to: "/help", label: t("footer.helpCenter") },
        { to: "/cancellation", label: t("footer.cancellationPolicy") },
        { to: "/confianza", label: "Centro de Confianza" },
        { to: "/privacidad", label: t("footer.privacy") },
        { to: "/terminos", label: t("footer.terms") },
      ],
    },
  ];

  return (
    <div className="relative isolate mt-14">
      {/* Smooth transition from previous section into the metallic footer */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-24 h-24 z-0"
        style={{
          background:
            "linear-gradient(to bottom, transparent 0%, rgba(10,6,4,0.55) 55%, #171717 100%)",
        }}
      />

      <footer
        className="relative overflow-hidden text-white"
        style={{
          background:
            "radial-gradient(120% 90% at 15% 0%, #4A2500 0%, #171717 45%, #171717 100%), linear-gradient(180deg, #171717 0%, #171717 100%)",
        }}
      >
        {/* Metallic top hairline */}
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-px"
          style={{
            background:
              "linear-gradient(to right, transparent 0%, rgba(251,191,36,0.55) 50%, transparent 100%)",
          }}
        />

        {/* Slow shimmer sweep across the metal surface */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.14] mm-footer-sheen"
          style={{
            background:
              "linear-gradient(115deg, transparent 30%, rgba(255,210,140,0.55) 50%, transparent 70%)",
            mixBlendMode: "screen",
          }}
        />

        {/* Faint radial glow from top center */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-64"
          style={{
            background:
              "radial-gradient(60% 100% at 50% 0%, rgba(251,146,60,0.18), transparent 70%)",
          }}
        />

        {/* Drifting gold particles */}
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          {[
            { l: "8%", d: "0s", s: 2 },
            { l: "22%", d: "3.2s", s: 1.5 },
            { l: "37%", d: "6s", s: 2.5 },
            { l: "54%", d: "1.4s", s: 1.5 },
            { l: "68%", d: "4.8s", s: 2 },
            { l: "82%", d: "2.2s", s: 1.5 },
            { l: "93%", d: "7.4s", s: 2 },
          ].map((p, i) => (
            <span
              key={i}
              className="mm-footer-particle absolute rounded-full"
              style={{
                left: p.l,
                width: p.s,
                height: p.s,
                background:
                  "radial-gradient(circle, rgba(253,224,150,0.9) 0%, rgba(251,146,60,0.35) 60%, transparent 100%)",
                animationDelay: p.d,
              }}
            />
          ))}
        </div>

        <div className="relative mx-auto max-w-7xl px-6 py-10 sm:px-8 sm:py-12 lg:px-12">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
            {/* Brand */}
            <div className="md:col-span-4 space-y-6">
              <div className="flex items-center gap-3">
                <BrandLockup onPhoto />
              </div>
              <p className="max-w-sm text-sm leading-relaxed text-white/60">
                {t("footer.tagline")}
              </p>
              <div className="flex gap-3">
                {[
                  { Icon: Instagram, href: SITE.social.instagram, label: "Instagram" },
                  { Icon: Facebook, href: SITE.social.facebook, label: "Facebook" },
                ]
                  .filter((s) => !!s.href)
                  .map(({ Icon, href, label }) => (
                    <a
                      key={label}
                      href={href!}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className="grid h-10 w-10 place-items-center rounded-full border border-amber-300/20 bg-white/[0.03] text-white/70 backdrop-blur-sm transition-all duration-300 hover:border-amber-300/60 hover:text-amber-300 hover:shadow-[0_0_20px_-4px_rgba(251,191,36,0.6)]"
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  ))}
              </div>
            </div>

            {/* Explorar */}
            <div className="md:col-span-2 space-y-5">
              <h4 className="text-[11px] font-black uppercase tracking-[0.22em] bg-gradient-to-r from-amber-200 via-amber-300 to-orange-400 bg-clip-text text-transparent">
                {cols[0].title}
              </h4>
              <ul className="space-y-3">
                {cols[0].links.map((l) => (
                  <li key={l.label}>
                    <a
                      href={l.to}
                      className="text-sm text-white/65 transition-colors hover:text-white"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Empresa */}
            <div className="md:col-span-2 space-y-5">
              <h4 className="text-[11px] font-black uppercase tracking-[0.22em] bg-gradient-to-r from-amber-200 via-amber-300 to-orange-400 bg-clip-text text-transparent">
                {cols[1].title}
              </h4>
              <ul className="space-y-3">
                {cols[1].links.map((l) => (
                  <li key={l.label}>
                    <a
                      href={l.to}
                      className="text-sm text-white/65 transition-colors hover:text-white"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Ayuda */}
            <div className="md:col-span-4 space-y-5">
              <h4 className="text-[11px] font-black uppercase tracking-[0.22em] bg-gradient-to-r from-amber-200 via-amber-300 to-orange-400 bg-clip-text text-transparent">
                {cols[2].title}
              </h4>
              <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                {cols[2].links.map((l) => (
                  <a
                    key={l.label}
                    href={l.to}
                    className="text-sm text-white/65 transition-colors hover:text-white"
                  >
                    {l.label}
                  </a>
                ))}
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new Event("mm:cookie-prefs"))}
                  className="text-left text-sm text-white/65 transition-colors hover:text-white"
                >
                  {t("footer.cookiePrefs", { defaultValue: "Preferencias de cookies" })}
                </button>
              </div>
            </div>
          </div>

          {/* Luminous divider */}
          <div
            aria-hidden
            className="mt-8 h-px w-full"
            style={{
              background:
                "linear-gradient(to right, transparent 0%, rgba(251,191,36,0.45) 20%, rgba(242,96,0,0.55) 50%, rgba(251,191,36,0.45) 80%, transparent 100%)",
            }}
          />

          <p className="mt-5 text-xs leading-relaxed text-hero-foreground">{SITE.legalFooter.text}</p>
          {SITE.legalFooter.rnt.trim() && <p className="mt-1 text-xs text-hero-foreground">RNT: {SITE.legalFooter.rnt}</p>}
          {/* Copyright row */}
          <div className="mt-6 flex flex-col items-start justify-between gap-4 text-xs text-white/45 sm:flex-row sm:items-center">
            <span className="tracking-tight">
              © {new Date().getFullYear()} Mostrando México. {t("footer.rights")}
            </span>
            <div className="flex flex-wrap items-center gap-5">
              <WhatsApp24 onDark />
              <span className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/55">
                  {t("footer.madeIn")}
                </span>
              </span>
            </div>
          </div>
        </div>

        {/* Bottom metallic flare */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-24"
          style={{
            background:
              "radial-gradient(60% 100% at 50% 100%, rgba(242,96,0,0.22), transparent 70%)",
          }}
        />
      </footer>

      <style>{`
        @keyframes mmFooterSheen {
          0% { transform: translateX(-40%); }
          100% { transform: translateX(40%); }
        }
        .mm-footer-sheen {
          animation: mmFooterSheen 14s ease-in-out infinite alternate;
          will-change: transform;
        }
        @keyframes mmFooterParticleRise {
          0% { transform: translateY(120%); opacity: 0; }
          15% { opacity: 1; }
          85% { opacity: 0.8; }
          100% { transform: translateY(-20%); opacity: 0; }
        }
        .mm-footer-particle {
          bottom: 0;
          animation: mmFooterParticleRise 12s linear infinite;
          will-change: transform, opacity;
        }
        @media (prefers-reduced-motion: reduce) {
          .mm-footer-sheen, .mm-footer-particle { animation: none; }
        }
      `}</style>
    </div>
  );
}
